import { useContext, useEffect, useMemo, useState } from 'react'
import { Subscription, combineLatest } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { LOGGER, indexBy, merge } from '@cicero/data/utils'
import { useObservable } from '@cicero/ui'
import { useModel } from './useModel'
import { AttributeValueDoc, AttributeValueSchema } from '../AttributeValueSchema'
import { PageDoc, PageSchema } from '../PageSchema'
import { IPageFilter, PageFilterOptionsContext } from '../filters'
import { mapFiltersToPageQuery } from './mapFiltersToPageQuery'

export const usePageFilterOptionsLookup = () => {
  const options = useContext(PageFilterOptionsContext)
  const optionLookup = useMemo(() => indexBy(options, 'id', true), [options])
  return optionLookup
}

export const usePageFilters = (filters: IPageFilter[], search?: string): [boolean, PageDoc[], AttributeValueDoc[]] => {
  const Pages = useModel(PageSchema)
  const AttributeValues = useModel(AttributeValueSchema)
  const optionLookup = usePageFilterOptionsLookup()
  const [pages, setPages] = useState<PageDoc[]>([])
  const [attributeValues, setAttributeValues] = useState<AttributeValueDoc[]>([])
  const isLoading = useObservable(() => combineLatest(Pages.loading$, AttributeValues.loading$).pipe(map(([l1, l2]) => l1 || l2)), false, [])

  useEffect(() => {
    let sub: Subscription
    const timeout = setTimeout(() => {
      const { pageQuery, attributeQuery } = mapFiltersToPageQuery(filters, optionLookup, search)

      if (attributeQuery) {
        // First load attribute values that match criteria, and get page_ids from those
        sub = AttributeValues.QUERY$(attributeQuery)
          .pipe(switchMap((vals) => {
            const page_ids = vals.reduce((acc, val) => { acc.add(val.page_id); return acc }, new Set())
            setAttributeValues(vals)
            return Pages.QUERY$(merge({ selector: { id: { $in: Array.from(page_ids) } } }, pageQuery || {}))
          }))
          .subscribe(p => setPages(p))
      } else {
        // Since no filters relate to attributes, just load pages directly
        sub = Pages.QUERY$(pageQuery || { selector: {} }).subscribe(p => setPages(p))
      }

      LOGGER.debug('Parsing new filters', { filters, optionLookup, attributeQuery, pageQuery })
    }, 100)

    return () => {
      if (timeout) { clearTimeout(timeout) }
      if (sub) { sub.unsubscribe() }
    }
  }, [search, filters, optionLookup])

  return [isLoading, pages, attributeValues]
}
